// Per-route JSON-LD, written into each prerendered page by scripts/prerender.js.
// One linked @graph per page: the business, the site, and the page itself.

import { faq, practices, serviceArea, toolkit } from './homeContent';
import { servicePages } from './navigation';
import { pageMeta } from './pageMeta';

export const SITE_URL = 'https://shumunovsolutions.com';

// Each route is served from <route>/index.html, so static hosts redirect /work to /work/
export const canonicalUrl = (path) => `${SITE_URL}${path.replace(/\/?$/, '/')}`;

const ids = {
  org: `${SITE_URL}/#organization`,
  website: `${SITE_URL}/#website`,
};

const organization = {
  '@type': ['ProfessionalService', 'Organization'],
  '@id': ids.org,
  name: 'Shumunov Solutions',
  url: SITE_URL,
  logo: `${SITE_URL}/logo-monogram.png`,
  image: `${SITE_URL}/og-image.png`,
  email: 'info@shumunovsolutions.com',
  slogan: 'Software, made to measure.',
  description: pageMeta['/'].description,
  address: {
    '@type': 'PostalAddress',
    addressLocality: 'Detroit',
    addressRegion: 'MI',
    addressCountry: 'US',
  },
  areaServed: [
    ...serviceArea.map((name) => ({ '@type': 'City', name: `${name}, MI` })),
    { '@type': 'AdministrativeArea', name: 'Oakland County, MI' },
    { '@type': 'AdministrativeArea', name: 'Macomb County, MI' },
    { '@type': 'AdministrativeArea', name: 'Wayne County, MI' },
    { '@type': 'Country', name: 'United States' },
  ],
  knowsAbout: [
    'Custom software development',
    'Legacy code modernization',
    'TypeScript migration',
    'Technical SEO',
    'Core Web Vitals',
    'Web accessibility',
    ...toolkit,
  ],
  hasOfferCatalog: {
    '@type': 'OfferCatalog',
    name: 'Software services',
    itemListElement: practices.map(({ name, body }) => ({
      '@type': 'Offer',
      itemOffered: { '@type': 'Service', name, description: body },
    })),
  },
  sameAs: ['https://github.com/shumunovsolutions'],
};

const website = {
  '@type': 'WebSite',
  '@id': ids.website,
  name: 'Shumunov Solutions',
  url: SITE_URL,
  inLanguage: 'en-US',
  publisher: { '@id': ids.org },
};

const crumbs = (path) => {
  const trail = [{ name: 'Home', path: '/' }];
  if (path.startsWith('/services')) trail.push({ name: 'Services', path: '/services' });
  const service = servicePages.find(({ to }) => to === path);
  if (service) trail.push({ name: service.label, path });
  else if (path !== '/' && path !== '/services') trail.push({ name: pageMeta[path].title.split(' | ')[0], path });
  return {
    '@type': 'BreadcrumbList',
    itemListElement: trail.map(({ name, path: p }, i) => ({
      '@type': 'ListItem',
      position: i + 1,
      name,
      item: canonicalUrl(p),
    })),
  };
};

/** JSON-LD for one route, or null for routes without page metadata. */
export function structuredData(path) {
  const meta = pageMeta[path];
  if (!meta) return null;
  const url = canonicalUrl(path);
  const service = servicePages.find(({ to }) => to === path);

  const page = {
    '@type': path === '/contact' ? 'ContactPage' : path === '/' ? ['WebPage', 'FAQPage'] : 'WebPage',
    '@id': `${url}#webpage`,
    url,
    name: meta.title,
    description: meta.description,
    isPartOf: { '@id': ids.website },
    about: { '@id': ids.org },
    breadcrumb: crumbs(path),
    ...(path === '/' && {
      mainEntity: faq.map(({ q, a }) => ({
        '@type': 'Question',
        name: q,
        acceptedAnswer: { '@type': 'Answer', text: a },
      })),
    }),
  };

  const graph = [organization, website, page];
  if (service)
    graph.push({
      '@type': 'Service',
      '@id': `${url}#service`,
      name: service.label,
      description: meta.description,
      serviceType: service.label,
      provider: { '@id': ids.org },
      areaServed: organization.areaServed,
      url,
    });

  return { '@context': 'https://schema.org', '@graph': graph };
}
